import type { ExperimentDef } from '../types';
import { runRc } from './engine';

export const rcCircuit: ExperimentDef = {
  id: 'rc_circuit',
  title: 'RC circuit',
  summary: 'Charge or discharge a capacitor through a resistor and time it. Vary resistance, capacitance, supply voltage and duration.',
  seriesKeys: ['t', 'voltage', 'current'],
  seriesLabels: { t: 'Time (s)', voltage: 'Capacitor voltage (V)', current: 'Current (mA)' },
  params: [
    {
      kind: 'number',
      key: 'resistance',
      label: 'Resistance',
      unit: 'kΩ',
      min: 0.1,
      max: 100,
      step: 0.1,
      default: 10,
      description: 'Series resistance in kilo-ohms, 0.1 to 100.',
    },
    {
      kind: 'number',
      key: 'capacitance',
      label: 'Capacitance',
      unit: 'µF',
      min: 1,
      max: 1000,
      step: 1,
      default: 100,
      description: 'Capacitance in microfarads, 1 to 1000.',
    },
    {
      kind: 'number',
      key: 'supply_voltage',
      label: 'Supply voltage',
      unit: 'V',
      min: 1,
      max: 24,
      step: 0.5,
      default: 9,
      description: 'Battery voltage in volts, 1 to 24. When discharging, the capacitor starts at this voltage.',
    },
    {
      kind: 'enum',
      key: 'mode',
      label: 'Mode',
      options: ['charge', 'discharge'],
      default: 'charge',
      description: 'charge: connect the empty capacitor to the battery. discharge: let the full capacitor drain through the resistor.',
    },
    {
      kind: 'number',
      key: 'duration',
      label: 'Duration',
      unit: 's',
      min: 0.5,
      max: 60,
      step: 0.5,
      default: 5,
      description: 'How long to record, in seconds, 0.5 to 60.',
    },
  ],
  measurements: [
    { key: 'time_constant_s', label: 'Time constant', unit: 's', description: 'Measured from the curve: time to complete 63.2% of the change. Empty if not reached.' },
    { key: 'rc_product_s', label: 'R·C', unit: 's', description: 'The theoretical time constant, resistance times capacitance.' },
    { key: 'half_time_s', label: 'Half-time', unit: 's', description: 'Time to complete half of the change.' },
    { key: 'final_voltage_v', label: 'Final voltage', unit: 'V', description: 'Capacitor voltage at the end of the recording.' },
    { key: 'initial_current_ma', label: 'Initial current', unit: 'mA', description: 'Current at the first instant, supply voltage over resistance.' },
    { key: 'energy_stored_mj', label: 'Energy stored', unit: 'mJ', description: 'Energy in the capacitor at the end, ½·C·V².' },
  ],
  run: runRc,
  noise: {
    time_constant_s: { resolution: 0.01, relative: 0.01 },
    half_time_s: { resolution: 0.01, relative: 0.01 },
    final_voltage_v: { resolution: 0.01, relative: 0.005 },
    initial_current_ma: { resolution: 0.01, relative: 0.01 },
    energy_stored_mj: { relative: 0.01 },
  },
  agentGuidance:
    'The time constant τ = R·C sets the pace: after one τ a charging capacitor reaches 63% of the supply and after five it is essentially full. τ is proportional to both R and C; the supply voltage changes the current and the energy but not τ. A discharge curve is a pure exponential, so fit_model on that trial with x t and y voltage returns k = −1/τ.',
};
